import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import compact from "lodash/compact";

// MUI
import { Grid } from "@mui/material";

import MultipleTextFieldGroup from "@/Layouts/components/MultipleTextFieldGroup/MultipleTextFieldGroup";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { ICITScheduledAccountNumbers } from "@/Settings/types/Common/model";
import InfoSubTitle from "../../../common/InfoSubTitle";
import Title from "../../../common/Title";
import { errorLogger } from "@/Common/helpers/logger";

type TScheduledAccountNumbersProps = {
	locationId: string;
	citScheduledAccountNumbers?: ICITScheduledAccountNumbers[];
};

const validateSimpleValue = (value: string) => {
	return value.length > 0;
};

const validateValues = (arr: string[]) => {
	if (arr.length !== [...new Set(arr)].length) {
		return false;
	}
	if (arr.length === 1) {
		const five = 5;
		return arr[0].length >= 1 && arr[0].length <= five;
	}
	return arr.every((item) => {
		return item.length >= 1;
	});
};

const ScheduledAccountNumbers = (props: TScheduledAccountNumbersProps) => {
	const { locationId, citScheduledAccountNumbers } = props;
	const [allData, setAllData] = useState<ICITScheduledAccountNumbers[]>([]);

	// Mutations
	const { mutate: mutateScheduledAccountNumbers, isPending: isLoadingAccounts } = useMutation({
		mutationKey: ["systemSettings", locationId],
		mutationFn: async (input: { citScheduledAccountNumbers: ICITScheduledAccountNumbers[] }) =>
			validateAllData() && updateSystemSettings(locationId, input, "SCHEDULED_ACCOUNT_NUMBERS"),
		onMutate: (input) => {
			// Optimistically update the data
			setAllData(input.citScheduledAccountNumbers);
		},
		onError: (error) => {
			errorLogger(error);
		},
	});

	const validateAllData = () => {
		return !allData.some((data) => !validateValues(data.values));
	};

	const onHandleActionUpdate = (value: string, from: string, idx: number) => {
		const allDataObject = allData.find((item) => item.label === from)?.values || [];
		if (value.length === 0 && allDataObject?.length > 1) {
			return;
		}

		if (!validateValues([value])) {
			return;
		}

		allDataObject[idx] = value;

		const newAllData = allData.map((item) => {
			if (item.label === from) {
				return {
					label: item.label,
					values: allDataObject,
				};
			}
			return item;
		});

		mutateScheduledAccountNumbers({
			citScheduledAccountNumbers: newAllData,
		});
	};

	// Handle on Add
	const handleOnAdd = (from: string) => {
		const newAllData = allData.map((item) => {
			if (item.label === from) {
				return {
					label: item.label,
					values: [...item.values, ""],
				};
			}
			return item;
		});
		setAllData(newAllData);
	};

	// Handle delete
	const handleOnDelete = (idx: number, from: string) => {
		const newAllData = allData.map((item) => {
			if (item.label === from) {
				return {
					label: item.label,
					values: compact(item.values.filter((_item, index) => idx !== index)),
				};
			}
			return item;
		});
		if (!isEqual(citScheduledAccountNumbers, newAllData)) {
			mutateScheduledAccountNumbers({
				citScheduledAccountNumbers: newAllData,
			});
		}
		setAllData(newAllData);
	};

	useEffect(() => {
		setAllData(citScheduledAccountNumbers || []);
	}, [citScheduledAccountNumbers]);

	return (
		<Grid container direction={"column"}>
			<Grid item>
				<Title title="Scheduled Account Numbers" className="!mb-[24px]" />
			</Grid>
			{allData?.map((data, index) => {
				const keyIndex = `account-numbers-${index}`;
				const isNotFirst = index !== 0;
				const classes = isNotFirst ? "mt-[24px] !mb-[12px]" : "!mb-[12px]";
				return (
					<Grid item key={keyIndex}>
						<InfoSubTitle title={data.label} className={classes} />
						<MultipleTextFieldGroup
							onAdd={() => handleOnAdd(data.label)}
							from={data.label}
							placeholder="Account Number"
							items={allData?.find((item) => item.label === data.label)?.values || []}
							onActionUpdate={onHandleActionUpdate}
							onDelete={handleOnDelete}
							simpleValidation={validateSimpleValue}
							validation={validateValues}
							isLoading={isLoadingAccounts}
						/>
					</Grid>
				);
			})}
		</Grid>
	);
};

export default ScheduledAccountNumbers;
